import {
  getBackLink,
  getBreadcrumbComponent,
  getBreadcrumbData,
  getBreadcrumbSecondLastLink,
} from '@/features/Breadcrumb/shared/Breadcrumb.utils';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useElasticRelated } from '@/features/shared/Hooks';
import { ColorNames, MAX_RELATED_CONTENT } from '@/utils/constants';
import { mapDocumentUrlToCategoryType } from '@/utils/helpers';
import { MetadataDocumentMapping } from '@/utils/mappings';
import {
  resolveBool,
  resolveCategories,
  resolveColorName,
  resolveContentBlocks,
  resolveDateObject,
  resolveId,
  resolveImage,
  resolveLink,
  resolvePerson,
  resolveReferences,
  resolveRelatedDocument,
  resolveRTC,
  resolveTheme,
} from '@/utils/resolvers';
import React, { useMemo, useRef, useState } from 'react';
import CMSWrapper from '../shared/CMSWrapper';
import { withGTM } from '../shared/HOCs';
import SEOHrefLang from '../shared/SEOHrefLang';
import BlogDetail from './BlogDetail';
import { onDatalayerSocialShare } from './BlogDetail.datalayer';

const resolveRelatedDocumentList = ({ rawVal, page, labels = {} }: any) => {
  return rawVal.map((item: any) => resolveRelatedDocument({ rawVal: item, page, labels }));
};

const replaceBlogLabel = ({ page, backLabel }: any) => {
  const breadCrumbCmp = getBreadcrumbComponent({ page });
  const hasBreadcrumb = Boolean(breadCrumbCmp);
  const breadcrumbData = hasBreadcrumb ? getBreadcrumbData({ breadCrumbCmp }) : undefined;
  const breadcrumbLink = hasBreadcrumb
    ? getBreadcrumbSecondLastLink({ breadcrumbData })
    : undefined;

  return hasBreadcrumb && breadcrumbLink
    ? getBackLink({ breadcrumbLink, backLabel, page })
    : undefined;
};

const mapKeysToTags = ({ keys = [], pageModels, url }: any) => {
  const categoryType = mapDocumentUrlToCategoryType({ url });
  const cardCategory = resolveCategories({ rawVal: keys, categoryType, pageModels });
  return cardCategory;
};

const MappedBlogDetail = ({ content, config, labels, contentButtons, pageModels, page }: any) => {
  const translationConfig = pageModels?.config?.translation;
  const [url] = useState(window.location.pathname);
  const { relatedContent, keys } = content || {};
  const relatedContentList = React.useMemo(
    () => !!relatedContent && resolveRelatedDocumentList({ rawVal: relatedContent, page, labels }),
    [page, relatedContent, labels],
  );

  const backgroundColor = useRef(config?.background_color || ColorNames.eggshell);
  const theme = useRef(resolveTheme('bg-color')({ rawVal: backgroundColor.current }));
  const cardColor = useRef(resolveColorName({ rawVal: theme.current }));
  const backLink = useRef(replaceBlogLabel({ page, backLabel: labels?.back_to || 'Back to %1' }));
  const tags = useMemo(() => mapKeysToTags({ keys, pageModels, url }), [keys, pageModels, url]);

  const hrefLangProps = React.useMemo(
    () => ({
      hrefLangsRaw: content?.pageMetadata?.hreflangUrl,
      exclusionPaths: translationConfig?.endpoint_exclusion_paths,
      countryCodesWhitelist: translationConfig?.country_codes_whitelist,
    }),
    [
      content?.pageMetadata?.hreflangUrl,
      translationConfig?.endpoint_exclusion_paths,
      translationConfig?.country_codes_whitelist,
    ],
  );

  const isElasticAllowed =
    !!content.isElasticAllowed && relatedContentList?.length < MAX_RELATED_CONTENT;

  const titleExclusion = React.useMemo(() => {
    return [
      ...(relatedContentList?.map(
        (record: any) => record?.metadataCompound?.searchDisplayTitle || record?.cardHeading,
      ) ?? []),
      content?.pageMetadata?.searchDisplayTitle || content?.title,
    ];
  }, [content?.pageMetadata?.searchDisplayTitle, content?.title, relatedContentList]);

  const { es, curatedRelatedContent, search, sortOptions, status, allLabels, engine, searchKey } =
    useElasticRelated({
      keys,
      pageModels,
      page,
      labels,
      isElasticAllowed,
      relatedContentList,
      contentCategoryFallback: ['Blogs'],
      sortBy: 'most-relevant',
      facetsList: ['topic'],
      excludedFilters: [{ title: titleExclusion }],
    });

  const blogDetailContent = React.useMemo(
    () => ({
      ...content,
      tags,
      relatedContent: curatedRelatedContent,
      contentButtons,
      backLink: backLink.current,
      backgroundColor: backgroundColor.current,
      cardColor: cardColor.current,
      labels,
      config,
    }),
    [config, content, contentButtons, labels, curatedRelatedContent, tags],
  );

  const elasticComponentContextValue = useMemo(
    () => ({
      queryParams: search,
      updateQueryParams: () => {},
      sortOptions,
      pagination: es.response.pagination,
      facets: es.facets,
      hideFilters: ['tax_content_category'],
      // eslint-disable-next-line no-nested-ternary
      status,
      results: es.response.results,
      labels: allLabels,
      manualUpdate: true,
      engine,
      requestID: es.response?.meta?.request_id,
      searchKey,
    }),
    [
      allLabels,
      engine,
      es.facets,
      es.response?.meta?.request_id,
      es.response.pagination,
      es.response.results,
      search,
      searchKey,
      sortOptions,
      status,
    ],
  );

  return (
    <div data-datalayer-position="blog_detail">
      <ElasticComponentContext.Provider value={elasticComponentContextValue}>
        <BlogDetail theme={theme.current} {...blogDetailContent} />
        <SEOHrefLang {...hrefLangProps} />
      </ElasticComponentContext.Provider>
    </div>
  );
};

const MappedSocialShareEvents = withGTM({
  Component: MappedBlogDetail,
  onDatalayer: onDatalayerSocialShare,
  config: {
    eventName: 'social-share-event',
    isCustom: true,
  },
});

const BlogDetailCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        title: null,
        shortDescription: [null, resolveRTC],
        publicationDate: [null, resolveDateObject],
        author: [null, resolvePerson],
        textLink: [null, resolveRTC],
        legacyContent: [null, resolveRTC],
        relatedContent: [null, resolveReferences],
        relatedContentLink: [null, resolveLink],
        socialShare: ['displaySocialShare', resolveBool],
        detailcontent: ['detailContent', resolveContentBlocks],
        metadataCompound: ['pageMetadata', MetadataDocumentMapping],
        keys: null,
        contentcategory: 'contentCategory',
        podcastSeries: 'podcastSeries',
        imagecompound: ['cardImage', resolveImage],
        id: [null, resolveId],
        useElasticSearch: ['isElasticAllowed', resolveBool],
      },
    ],
    'blogDetailPage.config': 'config',
    'blogDetailPage.labels': 'labels',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedSocialShareEvents}
      mapping={mapping.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default BlogDetailCMSWrapper;
